import React, { useState, useEffect } from 'react';
import { Button, Box, Typography, CircularProgress, IconButton } from '@mui/material';
import { CloudUpload, Delete } from '@mui/icons-material';
import { uploadImage } from '../../services/cloudinaryService';

interface ImageUploadProps {
  onImageUploaded: (imageUrl: string) => void;
  initialImage?: string;
  label?: string;
  folder?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  onImageUploaded,
  initialImage = '',
  label = 'تحميل صورة',
  folder
}) => {
  const [image, setImage] = useState<string>(initialImage);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // للتتبع في حالة وجود صورة أولية
  useEffect(() => {
    if (initialImage) {
      console.log('تم تعيين صورة أولية:', initialImage);
      setImage(initialImage);
    }
  }, [initialImage]);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    console.log('تم اختيار صورة:', file.name, 'حجم:', (file.size / 1024).toFixed(2) + 'KB', 'نوع:', file.type);
    
    // التحقق من نوع الملف
    if (!file.type.includes('image')) {
      console.error('خطأ: الملف ليس صورة:', file.type);
      setError('يرجى تحميل ملف صورة فقط');
      return;
    }
    
    // التحقق من حجم الملف (5MB كحد أقصى)
    if (file.size > 5 * 1024 * 1024) {
      console.error('خطأ: حجم الصورة كبير جدًا:', (file.size / (1024 * 1024)).toFixed(2) + 'MB');
      setError('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // تحميل الصورة إلى الخدمة المحددة في الإعدادات
      console.log('بدء تحميل الصورة إلى الخادم، المجلد:', folder || 'default');
      const imageUrl = await uploadImage(file, folder);
      
      if (!imageUrl) {
        throw new Error('لم يتم استلام رابط للصورة المحملة');
      }
      
      console.log('تم تحميل الصورة بنجاح، الرابط:', imageUrl);
      
      setImage(imageUrl);
      onImageUploaded(imageUrl);
      
      console.log('تم تحديث واجهة المستخدم بالصورة الجديدة');
    } catch (err) {
      console.error('خطأ في تحميل الصورة:', err);
      
      // تفاصيل أكثر عن الخطأ
      if (err instanceof Error) {
        console.error('رسالة الخطأ:', err.message);
        console.error('تتبع الخطأ:', err.stack);
        setError(`حدث خطأ أثناء تحميل الصورة: ${err.message}`);
      } else {
        setError('حدث خطأ أثناء تحميل الصورة. يرجى المحاولة مرة أخرى.');
      }
    } finally {
      setLoading(false);
      // إعادة تعيين قيمة عنصر الإدخال ليسمح بإعادة اختيار نفس الصورة
      if (e.target) {
        e.target.value = '';
      }
    }
  };

  const handleRemoveImage = () => {
    console.log('إزالة الصورة الحالية:', image);
    setImage('');
    onImageUploaded('');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <Typography variant="subtitle1">{label}</Typography>
      
      {image ? (
        <Box sx={{ position: 'relative', width: '100%', maxWidth: 300 }}>
          <img 
            src={image} 
            alt="Uploaded" 
            style={{ width: '100%', borderRadius: 8 }} 
            onError={(e) => {
              console.error('خطأ في تحميل الصورة من الرابط:', image);
              // تغيير المصدر إلى صورة بديلة في حالة فشل التحميل
              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x200?text=Error+Loading+Image';
              setError('تعذر عرض الصورة المحملة. قد تكون غير متاحة حالياً.');
            }}
          />
          <IconButton
            sx={{ 
              position: 'absolute', 
              top: 8, 
              right: 8, 
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' }
            }}
            onClick={handleRemoveImage}
          >
            <Delete color="error" />
          </IconButton>
        </Box>
      ) : (
        <Button
          component="label"
          variant="outlined"
          startIcon={loading ? <CircularProgress size={20} /> : <CloudUpload />}
          disabled={loading}
          sx={{ 
            p: 2, 
            border: '2px dashed', 
            borderColor: 'primary.main',
            borderRadius: 2,
            width: '100%',
            maxWidth: 300,
            height: 150,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1
          }}
        >
          {loading ? 'جاري التحميل...' : 'اختر صورة للتحميل'}
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={handleImageChange}
            onClick={(e) => {
              // ضمان أن القيمة فارغة للسماح بتحديد نفس الصورة مرة أخرى
              (e.target as HTMLInputElement).value = '';
            }}
          />
        </Button>
      )}
      
      {error && (
        <Typography color="error" variant="caption" sx={{ maxWidth: 300, textAlign: 'center' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default ImageUpload; 